// src/lib/api/utilities/stubs.js
// Stub methods for entities that don't have full API implementations yet

import { withErrorHandling, createSuccessResponse } from '../shared/errors.js'
import { logger } from '../../logger'
import { supabase } from '../../supabase'

// Magic Links - placeholder implementation
const _getMagicLinks = async (companyId, options = {}) => {
  logger.utilities('getMagicLinks called with companyId:', companyId)
  // TODO: Implement actual magic links fetching
  return []
}

// AI Conversations - fetch from database
const _getAiConversations = async (companyId, options = {}) => {
  logger.utilities('getAiConversations called with companyId:', companyId)
  
  if (!companyId) {
    logger.utilities('No company ID provided for getAiConversations')
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('ai_conversations')
    .select('*')
    .eq('company_id', companyId)
    .order('last_message_at', { ascending: false })
  
  if (error) {
    logger.error('[getAiConversations] Error fetching conversations:', error)
    throw error
  }
  
  logger.utilities('getAiConversations success, found', data?.length || 0, 'conversations')
  return createSuccessResponse(data || [])
}

// AI Messages - fetch from database (optionally filtered by conversation_id)
const _getAiMessages = async (companyId, options = {}) => {
  logger.utilities('getAiMessages called with companyId:', companyId, 'options:', options)
  
  if (!companyId) {
    logger.utilities('No company ID provided for getAiMessages')
    return createSuccessResponse([])
  }

  let query = supabase
    .from('ai_messages')
    .select('role, content, attachments, created_at, conversation_id')
    .order('created_at', { ascending: true })

  // If conversation_id is provided, filter by it
  if (options.conversationId) {
    query = query.eq('conversation_id', options.conversationId)
  } else {
    // Otherwise, filter by company_id through conversations
    // This requires a join, so we'll get all messages for conversations in this company
    const { data: conversations } = await supabase
      .from('ai_conversations')
      .select('id')
      .eq('company_id', companyId)
    
    if (conversations && conversations.length > 0) {
      const conversationIds = conversations.map(c => c.id)
      query = query.in('conversation_id', conversationIds)
    } else {
      // No conversations, return empty
      return createSuccessResponse([])
    }
  }
  
  const { data, error } = await query
  
  if (error) {
    logger.error('[getAiMessages] Error fetching messages:', error)
    throw error
  }
  
  // Log detailed info about attachments for debugging price assist issues
  if (data && data.length > 0) {
    const messagesWithAttachments = data.filter(m => m.attachments && m.attachments.length > 0)
    if (messagesWithAttachments.length > 0) {
      console.log('[getAiMessages] Messages with attachments:', messagesWithAttachments.length)
      messagesWithAttachments.forEach((msg, idx) => {
        console.log(`[getAiMessages] Message ${idx + 1} attachments:`, {
          role: msg.role,
          attachmentsCount: msg.attachments.length,
          types: msg.attachments.map(a => a.type),
          hasPriceAssistData: msg.attachments.some(a => a.type === 'price_assist_data')
        })
      })
    }
  }
  
  logger.utilities('getAiMessages success, found', data?.length || 0, 'messages')
  return createSuccessResponse(data || [])
}

// Files - placeholder implementation
const _getFiles = async (companyId, options = {}) => {
  logger.utilities('getFiles called with companyId:', companyId)
  // TODO: Implement actual files fetching
  return []
}

// Audit Logs - placeholder implementation
const _getAuditLogs = async (companyId, options = {}) => {
  logger.utilities('getAuditLogs called with companyId:', companyId)
  // TODO: Implement actual audit logs fetching
  return []
}

// Webhook Events - placeholder implementation
const _getWebhookEvents = async (companyId, options = {}) => {
  logger.utilities('getWebhookEvents called with companyId:', companyId)
  // TODO: Implement actual webhook events fetching
  return []
}

// Cache Invalidation - placeholder implementation
const _getCacheInvalidation = async (companyId, options = {}) => {
  logger.utilities('getCacheInvalidation called with companyId:', companyId)
  // TODO: Implement actual cache invalidation fetching
  return []
}

// Export with error handling
export const getMagicLinks = withErrorHandling(_getMagicLinks, 'get magic links', 'magic_links')
export const getAiConversations = withErrorHandling(_getAiConversations, 'get AI conversations', 'ai_conversations')
export const getAiMessages = withErrorHandling(_getAiMessages, 'get AI messages', 'ai_messages')
export const getFiles = withErrorHandling(_getFiles, 'get files', 'files')
export const getAuditLogs = withErrorHandling(_getAuditLogs, 'get audit logs', 'audit_logs')
export const getWebhookEvents = withErrorHandling(_getWebhookEvents, 'get webhook events', 'webhook_events')
export const getCacheInvalidation = withErrorHandling(_getCacheInvalidation, 'get cache invalidation', 'cache_invalidation')